import { ALPHA_2_ISO_3166_MATCH_NUMERIC_ISO_3166 } from 'cpool-constants';
import { z } from 'zod';

import { schemaConsorsAcademicTitle } from './academicTitle.schema';
import { schemaConsorsBirthName } from './birthName.schema';
import { schemaConsorsCity } from './city.schema';
import { schemaConsorsContactAddressMain } from './contactAddressMain.schema';
import { schemaConsorsCustomerAdvertisementConsent } from './customerAdvertisementConsent.schema';
import { schemaConsorsDateYYYYMMDD } from './date-yyyy-mm-dd.schema';
import { schemaConsorsDateYYYYMM } from './date-yyyy-mm.schema';
import { schemaConsorsEmployerName } from './employerName.schema';
import { schemaConsorsFamilyStatus } from './familyStatus.schema';
import { schemaConsorsFirstLastName } from './firstLastName.schema';
import { schemaConsorsGender } from './gender.schema';
import { schemaConsorsHousingSituation } from './housingSituation.schema';
import { schemaConsorsIdentificationNumber } from './identificationNumber.schema';
import { schemaConsorsIdentificationType } from './identificationType.schema';
import { schemaConsorsIndustry } from './industry.schema';
import { schemaConsorsIssuingAuthority } from './issuingAuthority.schema';
import { schemaConsorsKycStatus } from './kycStatus.schema';
import { schemaConsorsNobilityTitle } from './nobilityTitle.schema';
import { schemaConsorsPlaceOfBirth } from './placeOfBirth.schema';
import { schemaConsorsPlaceOfIssue } from './placeOfIssue.schema';
import { schemaConsorsProfession } from './profession.schema';
import { schemaConsorsRealEstate } from './realEstate.schema';
import { schemaConsorsResidencePermitType } from './residencePermitType.schema';
import { schemaConsorsStreet } from './street.schema';
import { schemaConsorsSubscriberIdentifierExternal } from './subscriberIdentifierExternal.schema';
import { schemaConsorsSubscriberIdentifierInternal } from './subscriberIdentifierInternal.schema';
import { schemaConsorsWarmRent } from './warmRent.schema';
import { schemaConsorsZipcode } from './zipcode.schema';

export const schemaConsorsSubscriberMain = z.object({
    academicTitle: schemaConsorsAcademicTitle.optional(),
    birthName: schemaConsorsBirthName.optional(),
    consents: z.object({
        customerAdvertisementConsent: schemaConsorsCustomerAdvertisementConsent.optional(),
        debtCoWish: z.boolean().optional(),
        isCustomerContactedForSubscriptionCompletion: z.boolean().optional(),
        isMscComplianceAgreed: z.boolean().optional(),
        schufaCallAllowed: z.boolean(),
    }),
    contactAddress: schemaConsorsContactAddressMain,
    countryOfBirth: z.nativeEnum(ALPHA_2_ISO_3166_MATCH_NUMERIC_ISO_3166).optional(),
    customerRating: z.number().int().min(0).max(99).optional(),
    dateOfBirth: schemaConsorsDateYYYYMMDD,
    employmentDetails: z
        .object({
            countryOfActivity: z.nativeEnum(ALPHA_2_ISO_3166_MATCH_NUMERIC_ISO_3166).optional(),
            employerAddress: z.object({
                employerCity: schemaConsorsCity.optional(),
                employerCountry: z.nativeEnum(ALPHA_2_ISO_3166_MATCH_NUMERIC_ISO_3166).optional(),
                employerName: schemaConsorsEmployerName.optional(),
                employerStreet: schemaConsorsStreet.optional(),
                employerZipcode: schemaConsorsZipcode.optional(),
            }),
            industry: schemaConsorsIndustry.optional(),
            profession: schemaConsorsProfession,
            professionBeginDate: schemaConsorsDateYYYYMM.optional(),
            professionEndDate: schemaConsorsDateYYYYMM.optional(),
        })
        .refine(
            (data) => {
                if (
                    (data.profession === 'SELF_EMPLOYED' || data.profession === 'MANAGING_DIRECTOR') &&
                    data.industry === undefined
                ) {
                    return false;
                }
                return true;
            },
            {
                message: 'Industry may not be undefined when profession is either SELF_EMPLOYED or MANAGING_DIRECTOR',
            },
        )
        .refine(
            (data) => {
                if (
                    /REGULAR_EMPLOYED|PUBLIC_SERVANT|WORKER|MANAGING_DIRECTOR|SELF_EMPLOYED/.test(data.profession) &&
                    data.professionBeginDate === undefined
                ) {
                    return false;
                }
                return true;
            },
            {
                message:
                    'professionBeginDate may not be undefined when profession is one of the following REGULAR_EMPLOYED, PUBLIC_SERVANT, WORKER, MANAGING_DIRECTOR or SELF_EMPLOYED',
            },
        )
        .refine(
            (data) => {
                if (
                    /REGULAR_EMPLOYED|PUBLIC_SERVANT|WORKER|MANAGING_DIRECTOR|SELF_EMPLOYED/.test(data.profession) &&
                    data.employerAddress.employerName === undefined
                ) {
                    return false;
                }
                return true;
            },
            {
                message:
                    'employerName may not be undefined when profession is one of the following REGULAR_EMPLOYED, PUBLIC_SERVANT, WORKER, MANAGING_DIRECTOR or SELF_EMPLOYED',
            },
        )
        .refine(
            (data) => {
                if (
                    /REGULAR_EMPLOYED|PUBLIC_SERVANT|WORKER|MANAGING_DIRECTOR|SELF_EMPLOYED/.test(data.profession) &&
                    data.employerAddress.employerStreet === undefined
                ) {
                    return false;
                }
                return true;
            },
            {
                message:
                    'employerStreet may not be undefined when profession is one of the following REGULAR_EMPLOYED, PUBLIC_SERVANT, WORKER, MANAGING_DIRECTOR or SELF_EMPLOYED',
            },
        )
        .refine(
            (data) => {
                if (
                    /REGULAR_EMPLOYED|PUBLIC_SERVANT|WORKER|MANAGING_DIRECTOR|SELF_EMPLOYED/.test(data.profession) &&
                    data.employerAddress.employerCity === undefined
                ) {
                    return false;
                }
                return true;
            },
            {
                message:
                    'employerCity may not be undefined when profession is one of the following REGULAR_EMPLOYED, PUBLIC_SERVANT, WORKER, MANAGING_DIRECTOR or SELF_EMPLOYED',
            },
        )
        .refine(
            (data) => {
                if (
                    /REGULAR_EMPLOYED|PUBLIC_SERVANT|WORKER|MANAGING_DIRECTOR|SELF_EMPLOYED/.test(data.profession) &&
                    data.employerAddress.employerZipcode === undefined
                ) {
                    return false;
                }
                return true;
            },
            {
                message:
                    'employerZipcode may not be undefined when profession is one of the following REGULAR_EMPLOYED, PUBLIC_SERVANT, WORKER, MANAGING_DIRECTOR or SELF_EMPLOYED',
            },
        )
        .refine(
            (data) => {
                if (
                    /REGULAR_EMPLOYED|PUBLIC_SERVANT|WORKER|MANAGING_DIRECTOR|SELF_EMPLOYED/.test(data.profession) &&
                    data.employerAddress.employerCountry === undefined
                ) {
                    return false;
                }
                return true;
            },
            {
                message:
                    'employerCountry may not be undefined when profession is one of the following REGULAR_EMPLOYED, PUBLIC_SERVANT, WORKER, MANAGING_DIRECTOR or SELF_EMPLOYED',
            },
        ),
    expense: z
        .object({
            creditsAnother: z.number().int().min(0).max(99999).optional(),
            hasResidentialProperty: z.boolean().optional(),
            otherHouseholdObligations: z.number().int().min(0).max(99999).optional(),
            privateHealthInsurance: z.boolean().optional(),
            realEstate: schemaConsorsRealEstate.optional(),
            spendingOnOtherChildren: z.number().int().min(0).max(999999).optional(),
            warmRent: schemaConsorsWarmRent.optional(),
        })
        .refine(
            (data) => {
                if (data.hasResidentialProperty === true && data.realEstate === undefined) {
                    return false;
                }
                return true;
            },
            { message: 'realEstate may not be undefined when hasResidentialProperty is true' },
        ),
    externalLoans: z
        .array(
            z.object({
                monthlyInstallment: z.number().int().min(0).max(9999999),
                outstandingAmount: z.number().int().min(0).max(9999999),
            }),
        )
        .min(0)
        .max(14)
        .optional(),
    familySituation: schemaConsorsFamilyStatus,
    financialLimit: z.number().int().min(0).max(99999).optional(),
    firstName: schemaConsorsFirstLastName,
    gender: schemaConsorsGender,
    germanTaxClass: z.number().int().min(1).max(6).optional(),
    germanTaxIdentifier: schemaConsorsPlaceOfIssue.optional(),
    housingSituation: schemaConsorsHousingSituation,
    identity: z
        .object({
            dateOfIssue: schemaConsorsDateYYYYMMDD.optional(),
            identificationNumber: schemaConsorsIdentificationNumber.optional(),
            identificationType: schemaConsorsIdentificationType.optional(),
            issuingAuthority: schemaConsorsIssuingAuthority.optional(),
            placeOfIssue: schemaConsorsPlaceOfIssue.optional(),
            residencePermitDateOfIssue: schemaConsorsDateYYYYMM.optional(),
            residencePermitType: schemaConsorsResidencePermitType.optional(),
            residencePermitValidTill: schemaConsorsDateYYYYMM
                .refine(
                    (value) => {
                        const [year, month] = value.split('-').map(Number) as [number, number];
                        const currentDate = new Date();
                        const currentYear = currentDate.getFullYear();
                        const currentMonth = currentDate.getMonth() + 1;

                        if (year < currentYear) {
                            return false;
                        }

                        if (year === currentYear && month <= currentMonth) {
                            return false;
                        }

                        return true;
                    },
                    {
                        message: 'Date must be at least one month in the future',
                    },
                )
                .optional(),
            validTill: schemaConsorsDateYYYYMMDD.optional(),
        })
        .refine(
            (data) => {
                if (data.residencePermitType !== undefined && data.identificationType === undefined) return false;
                return true;
            },
            { message: 'identificationType may not be undefined when residencePermitType is defined' },
        )
        .refine(
            (data) => {
                if (
                    data.residencePermitType === 'LIMITED_RESIDENCE_PERMIT_PARAGRAPH_19A' &&
                    data.residencePermitValidTill === undefined
                )
                    return false;
                return true;
            },
            {
                message:
                    'identity.residencePermitValidTill may not be undefined when residencePermitType is LIMITED_RESIDENCE_PERMIT_PARAGRAPH_19A',
            },
        ),
    income: z.object({
        additionalIncome: z.number().int().min(0).max(999999).optional(),
        childTaxAllowanceFactor: z.number().int().min(0).max(99).optional(),
        incomeChildAlimony: z.number().int().min(0).max(999999).optional(),
        isChildBenefitInSalery: z.boolean().optional(),
        netIncome: z.number().int().min(0).max(999999),
        otherIncome: z.number().int().min(0).max(999999).optional(),
        partnerIncome: z.number().int().min(0).max(999999).optional(),
        rentIncome: z.number().int().min(0).max(999999).optional(),
        retirementPensionSupplement: z.number().int().min(0).max(999999).optional(),
    }),
    interestCap: z.number().int().min(0).max(99).optional(),
    lastName: schemaConsorsFirstLastName,
    legitimationInfo: z.object({
        kycStatus: schemaConsorsKycStatus.optional(),
        legitimationFlag: z.boolean().optional(),
    }),
    nationality: z.nativeEnum(ALPHA_2_ISO_3166_MATCH_NUMERIC_ISO_3166),
    nobilityTitle: schemaConsorsNobilityTitle.optional(),
    numberOfChildren: z.number().int().min(0).max(9),
    placeOfBirth: schemaConsorsPlaceOfBirth,
    previousAddress: z.object({
        city: schemaConsorsCity.optional(),
        street: schemaConsorsStreet.optional(),
        zipcode: schemaConsorsZipcode.optional(),
    }),
    rolePlaying: z.literal('MAIN'),
    subscriberIdentifierExternal: schemaConsorsSubscriberIdentifierExternal.optional(),
    subscriberIdentifierInternal: schemaConsorsSubscriberIdentifierInternal.optional(),
});

export type SchemaConsorsSubscriberMain = z.infer<typeof schemaConsorsSubscriberMain>;
